// src/pages/tour-packages/hooks/tourRatePricing.ts
//
// Mode-aware rate pricing helpers shared by the create and edit tour-package
// forms. Group tours price by occupancy (double/triple); private tours price by
// group size (2..6 pax). CWB/CNB/Infant columns are shared across both modes.

import type { TourPackageFormValues } from "./useTourPackageCreateForm";

type RateValues = TourPackageFormValues["tourRates"][number];

const toNum = (v: number | "" | null | undefined): number | null =>
  v === "" || v == null ? null : Number(v);

export type RatePricePayload = {
  priceDouble: number;
  priceTriple: number | null;
  priceChildWb: number | null;
  priceChildWob: number | null;
  priceInfant: number | null;
  price2Pax: number | null;
  price3Pax: number | null;
  price4Pax: number | null;
  price5Pax: number | null;
  price6Pax: number | null;
};

/**
 * Build the mode-aware price columns for a rate.
 * - GROUP   → double/triple (+ shared child/infant); pax tiers null.
 * - PRIVATE → 2..6 pax (+ shared child/infant); triple null; the 2 Pax price is
 *   mirrored into priceDouble, which the storefront uses as the headline "from"
 *   price (the column is NOT NULL).
 */
export function buildRatePrices(
  r: RateValues,
  tourMode: "GROUP" | "PRIVATE"
): RatePricePayload {
  const childWb = toNum(r.priceChildWb);
  const childWob = toNum(r.priceChildWob);
  const infant = toNum(r.priceInfant);

  if (tourMode === "PRIVATE") {
    const p2 = toNum(r.price2Pax);
    return {
      priceDouble: p2 ?? 0, // mirror 2 Pax into the base/headline price
      priceTriple: null,
      priceChildWb: childWb,
      priceChildWob: childWob,
      priceInfant: infant,
      price2Pax: p2,
      price3Pax: toNum(r.price3Pax),
      price4Pax: toNum(r.price4Pax),
      price5Pax: toNum(r.price5Pax),
      price6Pax: toNum(r.price6Pax),
    };
  }

  return {
    priceDouble: toNum(r.priceDouble) ?? 0,
    priceTriple: toNum(r.priceTriple),
    priceChildWb: childWb,
    priceChildWob: childWob,
    priceInfant: infant,
    price2Pax: null,
    price3Pax: null,
    price4Pax: null,
    price5Pax: null,
    price6Pax: null,
  };
}

const GROUP_REQUIRED = [
  "priceDouble",
  "priceTriple",
  "priceChildWb",
  "priceChildWob",
  "priceInfant",
] as const;

const PRIVATE_REQUIRED = [
  "price2Pax",
  "price3Pax",
  "price4Pax",
  "price5Pax",
  "price6Pax",
  "priceChildWb",
  "priceChildWob",
  "priceInfant",
] as const;

/** True when any required price field for the active tour mode is empty. */
export function isMissingModePrices(values: TourPackageFormValues): boolean {
  return getMissingModePriceFields(values).length > 0;
}

/** The required price field keys for the active tour mode that are empty/invalid. */
export function getMissingModePriceFields(values: TourPackageFormValues): string[] {
  const required =
    values.tour.tourMode === "PRIVATE" ? PRIVATE_REQUIRED : GROUP_REQUIRED;
  const r = values.tourRates?.[0];
  if (!r) return [...required];
  return required.filter((k) => {
    const v = (r as any)[k];
    return v === "" || v == null || Number.isNaN(Number(v));
  });
}

/** Every price field key used by either mode (for clearing stale errors). */
export const ALL_MODE_PRICE_FIELDS: string[] = Array.from(
  new Set<string>([...GROUP_REQUIRED, ...PRIVATE_REQUIRED]),
);
